import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  ArrowLeft, 
  Receipt, 
  Loader2, 
  ChevronLeft, 
  ChevronRight, 
  FileText,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { format } from 'date-fns';

const PAGE_SIZE = 20;

const PaymentHistory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [shop, setShop] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    totalResults: 0,
    totalPages: 0,
    currentPage: 1,
    limit: PAGE_SIZE,
    skip: 0,
    hasMore: false,
  });

  const fetchPaymentHistory = async (page = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const skip = (page - 1) * PAGE_SIZE;
      const response = await apiService.getPaymentHistory(id, {
        limit: PAGE_SIZE,
        skip,
      });
      
      if (response.success) {
        setShop(response.data.shop);
        setPayments(response.data.paymentHistory || []);
        setPagination(prev => ({
          ...prev,
          ...response.data.pagination,
          currentPage: page,
        }));
      } else {
        setError(response.message || 'Failed to fetch payment history');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch payment history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentHistory();
  }, [id]);

  const handlePageChange = (newPage) => {
    fetchPaymentHistory(newPage);
  };

  // Open receipt PDF in new tab
  const handleDownloadReceipt = (transactionId) => {
    const receiptUrl = apiService.getReceiptUrl(transactionId);
    window.open(receiptUrl, '_blank');
  };

  const getPaymentStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'paid':
      case 'completed':
        return 'bg-[#22C55E]/20 text-[#4ADE80] border-[#22C55E]/30';
      case 'pending':
        return 'bg-[#F59E0B]/20 text-[#FBBF24] border-[#F59E0B]/30';
      case 'failed':
      case 'cancelled':
        return 'bg-[#EF4444]/20 text-[#F87171] border-[#EF4444]/30';
      default:
        return 'bg-[#334155] text-[#94A3B8] border-[#475569]';
    }
  };

  const getDirectionStyle = (direction) => {
    switch (direction?.toUpperCase()) {
      case 'CREDIT':
        return 'bg-[#22C55E]/20 text-[#4ADE80] border-[#22C55E]/30';
      case 'DEBIT':
        return 'bg-[#EF4444]/20 text-[#F87171] border-[#EF4444]/30';
      default:
        return 'bg-[#334155] text-[#94A3B8] border-[#475569]';
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy hh:mm a');
    } catch {
      return 'N/A';
    }
  };

  // Calculate totals - determine direction from eventType if not provided
  const getPaymentDirection = (payment) => {
    if (payment.direction || payment.label) return payment.direction || payment.label;
    if (payment.eventType) {
      if (payment.eventType.includes('RECHARGE') || payment.eventType.includes('CREDIT')) {
        return 'CREDIT';
      }
    }
    return 'DEBIT'; // Default to DEBIT for payments
  };

  const totalCredit = payments
    .filter(p => getPaymentDirection(p) === 'CREDIT')
    .reduce((sum, p) => sum + (p.amount || 0), 0);
  
  const totalDebit = payments
    .filter(p => getPaymentDirection(p) === 'DEBIT')
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  if (loading && payments.length === 0) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#2563EB] animate-spin mx-auto" />
          <p className="text-[#94A3B8] mt-2">Loading payment history...</p>
        </div>
      </div>
    );
  }

  if (error && !shop) {
    return (
      <div className="p-6 md:p-8" data-testid="error-state">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-[#E2E8F0] mb-2">{error}</h2>
          <p className="text-[#94A3B8] mb-4">Could not load payment history.</p>
          <Button 
            onClick={() => navigate('/shops')}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
          >
            Back to Shops
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="payment-history-page">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate(`/shops/${id}`)}
          className="text-[#94A3B8] hover:text-[#E2E8F0] hover:bg-[#334155]/50 mb-4 -ml-4"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to {shop?.shopName || 'Shop'}
        </Button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#EF4444]/20 flex items-center justify-center">
            <Receipt className="w-6 h-6 text-[#F87171]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#E2E8F0] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Payment History
            </h1>
            <p className="text-[#94A3B8]">{shop?.shopName || 'Loading...'}</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-4">
          <p className="text-[#64748B] text-xs uppercase tracking-wider mb-1">Total Transactions</p>
          <p className="text-2xl font-bold text-[#E2E8F0]">{pagination.totalResults || payments.length}</p>
        </div>
        <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-[#4ADE80]" />
            <p className="text-[#64748B] text-xs uppercase tracking-wider">Total Credit</p>
          </div>
          <p className="text-2xl font-bold text-[#4ADE80]">{formatCurrency(totalCredit)}</p>
        </div>
        <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown className="w-4 h-4 text-[#F87171]" />
            <p className="text-[#64748B] text-xs uppercase tracking-wider">Total Debit</p>
          </div>
          <p className="text-2xl font-bold text-[#F87171]">{formatCurrency(totalDebit)}</p>
        </div>
        <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-4">
          <p className="text-[#64748B] text-xs uppercase tracking-wider mb-1">Shop ID</p>
          <p className="text-2xl font-bold text-[#E2E8F0]">{shop?.shopId || 'N/A'}</p>
        </div>
      </div>

      {/* Payment History Table */}
      <div className="bg-[#1E293B] border border-[#334155] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-[#334155] hover:bg-transparent">
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">Date</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">Description</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">Type</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">Direction</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider text-right">Amount</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider text-center">Receipt</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <Loader2 className="w-6 h-6 text-[#2563EB] animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-[#64748B]">
                    No payment history available.
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((payment, index) => {
                  // Determine direction based on eventType if direction is not provided
                  let direction = payment.direction || payment.label;
                  if (!direction && payment.eventType) {
                    if (payment.eventType.includes('RECHARGE') || payment.eventType.includes('CREDIT')) {
                      direction = 'CREDIT';
                    } else if (payment.eventType.includes('SUBSCRIPTION') || payment.eventType.includes('DEBIT') || payment.eventType.includes('PAYMENT')) {
                      direction = 'DEBIT';
                    }
                  }
                  
                  return (
                  <TableRow key={payment._id || index} className="border-b border-[#334155]/50 hover:bg-[#334155]/20">
                    <TableCell className="text-[#94A3B8] whitespace-nowrap">
                      {formatDate(payment.createdAt || payment.paidAt)}
                    </TableCell>
                    <TableCell className="text-[#E2E8F0]">
                      <div>
                        <p className="font-medium">{payment.title || payment.description || 'Transaction'}</p>
                        {payment.packName && (
                          <p className="text-xs text-[#64748B]">{payment.packName}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {payment.eventType && (
                        <Badge variant="outline" className="bg-[#334155]/50 text-[#94A3B8] border-[#475569] text-xs">
                          {payment.eventType.replace(/_/g, ' ')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${getDirectionStyle(direction)} border`}>
                        {direction || 'N/A'}
                      </Badge>
                    </TableCell>
                    <TableCell className={`text-right font-medium whitespace-nowrap ${direction === 'CREDIT' ? 'text-[#4ADE80]' : 'text-[#F87171]'}`}>
                      {direction === 'CREDIT' ? '+' : '-'}{formatCurrency(payment.amount, payment.currency)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${getPaymentStatusStyle(payment.status || payment.transactionStatus)} border`}>
                        {payment.status || payment.transactionStatus || 'N/A'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownloadReceipt(payment._id)}
                        className="h-8 w-8 p-0 text-[#60A5FA] hover:text-[#93C5FD] hover:bg-[#2563EB]/20"
                        title="Download Receipt"
                        data-testid={`receipt-btn-${payment._id}`}
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )})
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {!loading && payments.length > 0 && pagination.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-[#64748B]">
            Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to {Math.min(pagination.currentPage * pagination.limit, pagination.totalResults)} of {pagination.totalResults} transactions
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage <= 1}
              className="border-[#334155] text-[#94A3B8] hover:bg-[#334155] disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-[#94A3B8] text-sm px-2">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasMore}
              className="border-[#334155] text-[#94A3B8] hover:bg-[#334155] disabled:opacity-50"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentHistory;
